import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiRefreshCw, FiLock } from 'react-icons/fi';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PIN_STORAGE_KEY = 'sahkolla_stats_pin';

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid ${({ theme }) => theme.border};

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const Timestamp = styled.div`
  margin-top: 0.2rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
`;

const Button = styled.button`
  min-height: 40px;
  padding: 0.5rem 0.8rem;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.bgSecondary};
  color: ${({ theme }) => theme.text};
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  font-weight: 650;
  border-radius: 0.25rem;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.hover};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LoginPanel = styled.form`
  width: 100%;
  max-width: 360px;
  margin: 2rem auto;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.bgSecondary};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Label = styled.label`
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.textSecondary};
`;

const PinInput = styled.input`
  min-height: 44px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.text};
  padding: 0.55rem 0.7rem;
  font-size: 1rem;
  border-radius: 0.25rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.accent}20;
  }
`;

const PrimaryButton = styled(Button)`
  justify-content: center;
  background: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.bg};
  border-color: ${({ theme }) => theme.accent};
`;

const Message = styled.div`
  padding: 0.75rem 0.9rem;
  border: 1px solid ${({ $error, theme }) => $error ? '#dc3545' : theme.border};
  color: ${({ $error, theme }) => $error ? '#dc3545' : theme.textSecondary};
  background: ${({ theme }) => theme.bgSecondary};
  font-size: 0.86rem;
  font-weight: 600;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Metric = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.bgSecondary};
  padding: 0.85rem;
`;

const MetricLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.textSecondary};
`;

const MetricValue = styled.div`
  margin-top: 0.3rem;
  font-size: 1.45rem;
  line-height: 1.1;
  font-weight: 800;
  color: ${({ theme }) => theme.text};
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.bgSecondary};
  min-width: 0;
`;

const PanelTitle = styled.h3`
  padding: 0.8rem 0.9rem;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text};
  font-size: 0.95rem;
  font-weight: 800;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.55rem 0.7rem;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.55rem 0.7rem;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text};
  vertical-align: top;
  white-space: nowrap;
`;

const Status = styled.span`
  color: ${({ $bad, theme }) => $bad ? '#dc3545' : theme.accent};
  font-weight: 800;
`;

const Empty = styled.div`
  padding: 0.85rem;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.85rem;
  font-weight: 600;
`;

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const numberFormat = new Intl.NumberFormat('fi-FI');

const formatNumber = (value) => numberFormat.format(value || 0);

const StatsDashboard = () => {
  const [pin, setPin] = useState(() => sessionStorage.getItem(PIN_STORAGE_KEY) || '');
  const [pinInput, setPinInput] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const latestRun = data?.scraper?.recent_runs?.[0];

  const failedSources = useMemo(() => {
    const sources = data?.scraper?.sources || [];
    return sources.filter((source) => source.last_status && source.last_status !== 'success');
  }, [data]);

  const loadStats = async (activePin = pin) => {
    if (!activePin) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/stats/dashboard`, {
        headers: {
          'X-Stats-Pin': activePin,
        },
        credentials: 'omit',
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(PIN_STORAGE_KEY);
          setPin('');
        }
        throw new Error(payload.error || `API request failed: ${response.status}`);
      }

      sessionStorage.setItem(PIN_STORAGE_KEY, activePin);
      setPin(activePin);
      setData(payload);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pin) {
      loadStats(pin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    loadStats(pinInput.trim());
  };

  if (!pin || (!data && error)) {
    return (
      <Shell>
        <LoginPanel onSubmit={handleSubmit}>
          <Label htmlFor="stats-pin">PIN</Label>
          <PinInput
            id="stats-pin"
            type="password"
            value={pinInput}
            onChange={(event) => setPinInput(event.target.value)}
            autoComplete="current-password"
            autoFocus
          />
          <PrimaryButton type="submit" disabled={loading || !pinInput.trim()}>
            <FiLock size={16} />
            {loading ? 'Tarkistetaan...' : 'Avaa'}
          </PrimaryButton>
          {error && <Message $error>{error}</Message>}
        </LoginPanel>
      </Shell>
    );
  }

  return (
    <Shell>
      <HeaderRow>
        <div>
          <Title>Stats</Title>
          <Timestamp>Päivitetty {formatDate(data?.generated_at)}</Timestamp>
        </div>
        <Button type="button" onClick={() => loadStats(pin)} disabled={loading}>
          <FiRefreshCw size={16} />
          {loading ? 'Päivitetään...' : 'Päivitä'}
        </Button>
      </HeaderRow>

      {error && <Message $error>{error}</Message>}

      {!data ? (
        <Message>Ladataan...</Message>
      ) : (
        <>
          <MetricGrid>
            <Metric>
              <MetricLabel>Uutiset</MetricLabel>
              <MetricValue>{formatNumber(data.articles?.total)}</MetricValue>
            </Metric>
            <Metric>
              <MetricLabel>Viimeisin uutinen</MetricLabel>
              <MetricValue>{formatDate(data.articles?.latest_publish_time)}</MetricValue>
            </Metric>
            <Metric>
              <MetricLabel>Avaukset 7 pv</MetricLabel>
              <MetricValue>{formatNumber(data.engagement?.opens_week)}</MetricValue>
            </Metric>
            <Metric>
              <MetricLabel>Scraper</MetricLabel>
              <MetricValue>{latestRun?.status || '-'}</MetricValue>
            </Metric>
          </MetricGrid>

          {!data.scraper?.health_tables_available && (
            <Message>
              Scraperin health-tauluja ei ole vielä backendin feed-tietokannassa.
            </Message>
          )}

          <SectionGrid>
            <Panel>
              <PanelTitle>Viimeisimmät ajot</PanelTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Aika</Th>
                      <Th>Status</Th>
                      <Th>Tark.</Th>
                      <Th>EV</Th>
                      <Th>AI</Th>
                      <Th>Virheet</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.scraper?.recent_runs || []).map((run) => (
                      <tr key={run.id}>
                        <Td>{formatDate(run.started_at)}</Td>
                        <Td>
                          <Status $bad={run.status !== 'success'}>{run.status}</Status>
                        </Td>
                        <Td>{formatNumber(run.entries_checked)}</Td>
                        <Td>{formatNumber(run.ev_related_entries)}</Td>
                        <Td>{formatNumber(run.ai_classification_calls)}</Td>
                        <Td>{formatNumber(run.errors)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
              {(data.scraper?.recent_runs || []).length === 0 && <Empty>Ei ajoja.</Empty>}
            </Panel>

            <Panel>
              <PanelTitle>Alueet</PanelTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Alue</Th>
                      <Th>Uutisia</Th>
                      <Th>Viimeisin</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.articles?.by_region || []).map((region) => (
                      <tr key={region.region}>
                        <Td>{region.region}</Td>
                        <Td>{formatNumber(region.count)}</Td>
                        <Td>{formatDate(region.latest_publish_time)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Panel>
          </SectionGrid>

          <Panel>
            <PanelTitle>Lähteet</PanelTitle>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Lähde</Th>
                    <Th>Status</Th>
                    <Th>Feed</Th>
                    <Th>Tark.</Th>
                    <Th>EV</Th>
                    <Th>Viive</Th>
                    <Th>Viimeisin</Th>
                  </tr>
                </thead>
                <tbody>
                  {(data.scraper?.sources || []).map((source) => (
                    <tr key={source.source}>
                      <Td>{source.name || source.source}</Td>
                      <Td>
                        <Status $bad={source.last_status !== 'success'}>{source.last_status || '-'}</Status>
                      </Td>
                      <Td>{formatNumber(source.feed_entries)}</Td>
                      <Td>{formatNumber(source.entries_checked)}</Td>
                      <Td>{formatNumber(source.ev_related_entries)}</Td>
                      <Td>{source.latency_ms == null ? '-' : `${formatNumber(source.latency_ms)} ms`}</Td>
                      <Td>{formatDate(source.latest_feed_publish_time)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            {(data.scraper?.sources || []).length === 0 && <Empty>Ei lähdetietoja.</Empty>}
            {failedSources.length > 0 && (
              <Message $error>
                {failedSources.length} lähdettä ei ole onnistuneessa tilassa.
              </Message>
            )}
          </Panel>

          <Panel>
            <PanelTitle>Artikkelit lähteittäin</PanelTitle>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Lähde</Th>
                    <Th>Alue</Th>
                    <Th>Uutisia</Th>
                    <Th>Viimeisin</Th>
                  </tr>
                </thead>
                <tbody>
                  {(data.articles?.by_source || []).map((source) => (
                    <tr key={`${source.source}-${source.region}`}>
                      <Td>{source.source}</Td>
                      <Td>{source.region}</Td>
                      <Td>{formatNumber(source.count)}</Td>
                      <Td>{formatDate(source.latest_publish_time)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </Panel>
        </>
      )}
    </Shell>
  );
};

export default StatsDashboard;
